<script setup>
import { computed, shallowRef, ref, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { priorityMeta, priorityRank } from '../constants/priorityColors';
import { useTaskDayDrag } from '../composables/useTaskDayDrag';

const props = defineProps({
  filters: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['taskOpen']);

const store = useStore();
const { t } = useI18n();

const MAX_VISIBLE = 3;
const DAY_HEADERS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

const currentDate = shallowRef(new Date());

function dateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dueDayKey(epochSeconds) {
  const d = new Date(epochSeconds * 1000);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

const monthLabel = computed(() =>
  currentDate.value.toLocaleDateString('pt-BR', {
    month: 'long',
    year: 'numeric',
  })
);

function goToPreviousMonth() {
  const d = new Date(currentDate.value);
  d.setMonth(d.getMonth() - 1);
  currentDate.value = d;
}

function goToNextMonth() {
  const d = new Date(currentDate.value);
  d.setMonth(d.getMonth() + 1);
  currentDate.value = d;
}

const weeks = computed(() => {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
  const start = new Date(firstDay);
  start.setDate(start.getDate() - startOffset);

  const cells = [];
  const cursor = new Date(start);
  while (cursor <= lastDay || cells.length % 7 !== 0) {
    cells.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
    if (cells.length > 42) break;
  }

  const rows = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }
  return rows;
});

function loadMonthTasks() {
  const year = currentDate.value.getFullYear();
  const month = currentDate.value.getMonth();
  const from = new Date(year, month, 1);
  const to = new Date(year, month + 1, 0);
  const pad = n => String(n).padStart(2, '0');
  const fmt = d =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  store.dispatch('tasks/fetchTasks', {
    ...props.filters,
    due_date_from: `${fmt(from)}T00:00:00`,
    due_date_to: `${fmt(to)}T23:59:59`,
  });
}

onMounted(loadMonthTasks);
watch(currentDate, loadMonthTasks);
watch(() => props.filters, loadMonthTasks, { deep: true });

const allTasks = computed(() => store.getters['tasks/allTasks'] ?? []);

function hasTime(task) {
  const d = new Date(task.due_date * 1000);
  return d.getHours() !== 0 || d.getMinutes() !== 0;
}

function timeLabel(task) {
  const d = new Date(task.due_date * 1000);
  const h = String(d.getHours()).padStart(2, '0');
  const m = String(d.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function sortTasks(list) {
  return [...list].sort((a, b) => {
    const aHasTime = hasTime(a);
    const bHasTime = hasTime(b);
    if (aHasTime && bHasTime) return a.due_date - b.due_date;
    if (aHasTime !== bHasTime) return aHasTime ? -1 : 1;
    return priorityRank(a.priority) - priorityRank(b.priority);
  });
}

function tasksForDay(day) {
  const key = dateKey(day);
  return sortTasks(
    allTasks.value.filter(
      task => task.due_date && dueDayKey(task.due_date) === key
    )
  );
}

function isCurrentMonth(day) {
  return day.getMonth() === currentDate.value.getMonth();
}

function isToday(day) {
  return dateKey(day) === dateKey(new Date());
}

function taskColor(task) {
  return priorityMeta(task.priority);
}

const dayCellRefs = shallowRef(new Map());

function setDayCellRef(day, el) {
  const key = dateKey(day);
  if (el) {
    dayCellRefs.value.set(key, el);
  } else {
    dayCellRefs.value.delete(key);
  }
}

const { onTaskMousedown, dragState } = useTaskDayDrag({
  dayCellRefs,
  priorityBarStyle: task => {
    const meta = taskColor(task);
    return {
      className: meta.bar ?? '',
      style: meta.barStyle ?? {},
    };
  },
});

// Popover de overflow
const overflowDayKey = ref(null);
const overflowStyle = ref({});
let overflowCloseTimer = null;

function openOverflow(day, cellEl) {
  clearTimeout(overflowCloseTimer);
  const rect = cellEl.getBoundingClientRect();
  const spaceBelow = window.innerHeight - rect.bottom;
  const opensUp = spaceBelow < 200;

  overflowDayKey.value = dateKey(day);
  overflowStyle.value = opensUp
    ? {
        bottom: `${window.innerHeight - rect.bottom + rect.height}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        minHeight: `${rect.height}px`,
      }
    : {
        top: `${rect.top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        minHeight: `${rect.height}px`,
      };
}

function scheduleCloseOverflow() {
  clearTimeout(overflowCloseTimer);
  overflowCloseTimer = setTimeout(() => {
    overflowDayKey.value = null;
  }, 150);
}

function cancelCloseOverflow() {
  clearTimeout(overflowCloseTimer);
}

function onOverflowTaskMousedown(event, task, cardEl) {
  onTaskMousedown(event, task, cardEl);
  overflowDayKey.value = null;
}

const overflowTasks = computed(() => {
  if (!overflowDayKey.value) return [];
  return sortTasks(
    allTasks.value.filter(
      task => task.due_date && dueDayKey(task.due_date) === overflowDayKey.value
    )
  );
});
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden">
    <!-- Navegação de mês -->
    <div
      class="flex items-center justify-center gap-4 py-2 border-b border-n-weak flex-shrink-0"
    >
      <button
        class="i-lucide-chevron-left size-4 text-n-slate-11 hover:text-n-slate-12"
        @click="goToPreviousMonth"
      />
      <span class="text-sm font-medium text-n-slate-12 min-w-40 text-center">
        {{ monthLabel }}
      </span>
      <button
        class="i-lucide-chevron-right size-4 text-n-slate-11 hover:text-n-slate-12"
        @click="goToNextMonth"
      />
    </div>

    <!-- Cabeçalhos dos dias da semana -->
    <div class="grid grid-cols-7 border-b border-n-weak flex-shrink-0">
      <div
        v-for="day in DAY_HEADERS"
        :key="day"
        class="py-2 text-center text-xs font-medium text-n-slate-10 uppercase"
      >
        {{ day }}
      </div>
    </div>

    <!-- Grid das semanas -->
    <div class="flex-1 grid auto-rows-fr overflow-y-auto">
      <div
        v-for="(week, wi) in weeks"
        :key="wi"
        class="grid grid-cols-7 border-b border-n-weak last:border-b-0 min-h-[100px]"
      >
        <div
          v-for="day in week"
          :key="dateKey(day)"
          :ref="el => setDayCellRef(day, el)"
          data-day-cell
          class="border-r border-n-weak last:border-r-0 p-1"
          :class="[
            !isCurrentMonth(day) ? 'bg-n-slate-3 text-n-slate-10' : '',
            dragState.targetDayKey === dateKey(day) ? 'bg-n-alpha-2' : '',
          ]"
        >
          <span
            class="text-xs font-medium inline-flex items-center justify-center size-6 rounded-full mb-1"
            :class="
              isToday(day)
                ? 'bg-n-brand text-white'
                : isCurrentMonth(day)
                  ? 'text-n-slate-12'
                  : 'text-n-slate-10'
            "
          >
            {{ day.getDate() }}
          </span>

          <!-- Chips de tarefas -->
          <div class="flex flex-col gap-0.5">
            <div
              v-for="task in tasksForDay(day).slice(0, MAX_VISIBLE)"
              :key="task.id"
              class="truncate rounded px-1 text-xs cursor-pointer text-white flex items-center gap-1"
              :class="[
                taskColor(task).bar,
                dragState.draggingId === task.id ? '!opacity-30' : '',
              ]"
              :style="taskColor(task).barStyle"
              @click.stop="emit('taskOpen', task.id)"
              @mousedown="onTaskMousedown($event, task, $event.currentTarget)"
            >
              <span v-if="hasTime(task)" class="flex-shrink-0 opacity-90">{{
                timeLabel(task)
              }}</span>
              <span class="truncate">{{ task.title }}</span>
            </div>
            <p
              v-if="tasksForDay(day).length > MAX_VISIBLE"
              class="text-xs text-n-slate-10 px-1 cursor-default"
              @mouseenter="
                openOverflow(
                  day,
                  $event.currentTarget.closest('[data-day-cell]')
                )
              "
              @mouseleave="scheduleCloseOverflow"
            >
              {{
                t('TASKS.CALENDAR_VIEW.MORE', {
                  count: tasksForDay(day).length - MAX_VISIBLE,
                })
              }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="overflowDayKey"
      class="fixed z-[9998] bg-n-background border border-n-weak rounded-lg shadow-xl p-1 flex flex-col gap-0.5"
      :style="overflowStyle"
      @mouseenter="cancelCloseOverflow"
      @mouseleave="scheduleCloseOverflow"
    >
      <div
        v-for="task in overflowTasks"
        :key="task.id"
        class="truncate rounded px-1 text-xs cursor-pointer text-white flex items-center gap-1"
        :class="[
          taskColor(task).bar,
          dragState.draggingId === task.id ? '!opacity-30' : '',
        ]"
        :style="taskColor(task).barStyle"
        @click.stop="emit('taskOpen', task.id)"
        @mousedown="onOverflowTaskMousedown($event, task, $event.currentTarget)"
      >
        <span v-if="hasTime(task)" class="flex-shrink-0 opacity-90">{{
          timeLabel(task)
        }}</span>
        <span class="truncate">{{ task.title }}</span>
      </div>
    </div>
  </Teleport>
</template>
